import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListPageLayoutFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListPageLayoutFragmentFragmentDoc';
import { DynamicYield } from '@packages/shared/src/ui/DynamicYield/DynamicYield';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import type { Device } from '@packages/themes/src/default';
import { usePaging } from '../../hooks/usePaging';
import { InfiniteProductGrid } from '../InfiniteProductGrid/InfiniteProductGrid';
import { ProductGrid } from '../ProductGrid/ProductGrid';
import { ProductListPagination } from '../ProductListPagination/ProductListPagination';
import type { FilterSettingsProps } from '../FilterSettings/FilterSettings';
import { FilterSettings } from '../FilterSettings/FilterSettings';
import { DisplaySettingsDesktop } from '../DisplaySettingsDesktop/DisplaySettingsDesktop';
import { DisplaySettingsMobile } from '../DisplaySettingsMobile/DisplaySettingsMobile';
import { SearchAdServerIntegration } from '../SearchAdServerIntegration/SearchAdServerIntegration';
import { SearchStructuredData } from '../SearchStructuredData/SearchStructuredData';
import { useSeenProductsTracking } from './useSeenProductsTracking';
import { getPageLeaveKey } from './getPageLeaveKey';
import { PRODUCT_LIST_ID } from '../../ids';
import { bypassProductListPageLayoutMessages } from '../../bypassBlocks.messages';

/* GraphQL */ `
  fragment ProductListPageLayoutFragment on SearchProductResult {
    ...FilterSettingsFragment
    ...UsePagingFragment
    ...DisplaySettingsDesktopFragment
    ...DisplaySettingsMobileFragment
    ...ProductListPaginationFragment
    ...ProductGridFragment
    ...InfiniteProductGridFragment
    ...SearchStructuredDataFragment
    summary {
      dominantProductGroup
    }

    ... on SuccessfulSearchResult {
      recoveryStrategy {
        ... on FilterAdjustment {
          removeFilters
        }
      }
    }
  }
`;

const messages = defineMessages({
  productListLabel: {
    id: 'search.main.productList.label',
    defaultMessage: 'Produktliste',
  },
});

export type ProductListPageLayoutProps = {
  maskedData: FragmentType<typeof ProductListPageLayoutFragmentFragmentDoc>;
  topLevelCategories?: string[];
  aboveMainContentComponent: ReactNode;
  leftSidebarDesktopComponent: ReactNode;
  renderCategoryDialogContent: FilterSettingsProps['renderCategoryDialogContent'];
  belowProductsComponent: ReactNode;
  device: Device;
  referrer?: string;
  leftSidebarAriaLabel: string;
};

/**
 * Shared layout for pages displaying product lists
 *
 * Includes the actual product grid, filtering, sorting, display mode, pagination, and slots for additional content.
 */
export const ProductListPageLayout: FC<ProductListPageLayoutProps> = ({
  maskedData,
  topLevelCategories,
  aboveMainContentComponent,
  leftSidebarDesktopComponent,
  renderCategoryDialogContent,
  belowProductsComponent,
  device,
  referrer,
  leftSidebarAriaLabel,
}) => {
  const data = unmask<typeof ProductListPageLayoutFragmentFragmentDoc>(maskedData);
  const { formatMessage } = useIntl();
  const { isDesktop } = useDeviceType();
  const url = useUrl();

  const pageLeaveKey = getPageLeaveKey(url);
  const { incrementNumberOfSeenProducts } = useSeenProductsTracking(pageLeaveKey);

  const { pagingMode, pageCount, currentPage } = usePaging(data);

  const {
    summary: { dominantProductGroup },
  } = data;

  const removeFilters =
    data.__typename === 'SuccessfulSearchResult' &&
    data.recoveryStrategy?.__typename === 'FilterAdjustment' &&
    data.recoveryStrategy.removeFilters;

  return (
    // position relative is necessary for the SearchAdServerIntegration component
    <div className="relative flex flex-col gap-1 p-1">
      <SearchStructuredData maskedData={data} />

      {aboveMainContentComponent}

      <BypassBlock buttonText={formatMessage(bypassProductListPageLayoutMessages.filter)}>
        <FilterSettings
          maskedData={data}
          showRemovedFiltersNotification={removeFilters}
          renderCategoryDialogContent={renderCategoryDialogContent}
        />

        {isDesktop ? (
          <DisplaySettingsDesktop maskedData={data} />
        ) : (
          <DisplaySettingsMobile maskedData={data} />
        )}
      </BypassBlock>

      <hr className="opacity-15" />

      <div className="grid [grid-auto-columns:1fr] grid-flow-col gap-1">
        <nav
          aria-controls={PRODUCT_LIST_ID}
          aria-label={leftSidebarAriaLabel}
          className="col-span-1 max-md:hidden"
        >
          {leftSidebarDesktopComponent}
        </nav>

        {/* NOTE: minWidth is required to properly propagate size constraints to RecoSlider and prevent the page from blowing up */}
        {/* see also: https://github.com/philipwalton/flexbugs#1-minimum-content-sizing-of-flex-items-not-honored */}
        <div className="col-span-3 flex min-w-0 flex-col lg:col-span-4">
          {/* NOTE: placeholder for dynamic yield category slider content, right above the product list */}
          <DynamicYield dataDyId="dy_productlist_categoryslider" />

          <section
            aria-label={formatMessage(messages.productListLabel)}
            id={PRODUCT_LIST_ID}
            className="flex flex-col gap-2"
          >
            <BypassBlock buttonText={formatMessage(bypassProductListPageLayoutMessages.products)}>
              {pagingMode === 'infinite' ? (
                <InfiniteProductGrid
                  maskedResult={data}
                  key={url}
                  device={device}
                  referrer={referrer}
                  topLevelCategories={topLevelCategories}
                  onProductFirstBecameVisible={incrementNumberOfSeenProducts}
                />
              ) : (
                <ProductGrid
                  maskedResult={data}
                  key={url}
                  pageNumber={currentPage}
                  topLevelCategories={topLevelCategories}
                  onProductFirstBecameVisible={incrementNumberOfSeenProducts}
                />
              )}
            </BypassBlock>
            {pageCount > 1 && pagingMode === 'pagination' && (
              <div className="self-center">
                <ProductListPagination maskedData={data} />
              </div>
            )}
            {belowProductsComponent}
          </section>
        </div>

        <ErrorBoundary>
          <Suspense>
            <SearchAdServerIntegration majorMkz={dominantProductGroup ?? undefined} />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};
